// Access generation for sensitive media (recordings, transcripts). The server stays the
// authority; this only guarantees the client never shows or keeps data after access may have
// changed. Every change bumps `epoch`; mounted views clear immediately, abort in-flight
// requests and refuse any response that started under an older epoch.
import type { ApiClient } from '../lib/api.ts';
import { isAbortError } from '../lib/api.ts';
import { describeError, type ErrorView } from '../lib/errorCopy.ts';

export type MediaAccessState = 'ready' | 'refreshing' | 'blocked';

export interface MediaAccessSnapshot {
  epoch: number;
  state: MediaAccessState;
  reason: string | null;
}

export class MediaAccess {
  private snapshot: MediaAccessSnapshot = { epoch: 1, state: 'ready', reason: null };
  private readonly listeners = new Set<() => void>();

  getSnapshot = (): MediaAccessSnapshot => this.snapshot;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  /** Access may have changed: clear everything now. `refreshing` until the session is re-read. */
  invalidate(state: 'refreshing' | 'blocked', reason: string | null): void {
    this.set({ epoch: this.snapshot.epoch + 1, state, reason });
  }

  /** The session (and so the permission set) was re-read; views may load again. */
  ready(): void {
    this.set({ epoch: this.snapshot.epoch + 1, state: 'ready', reason: null });
  }

  private set(next: MediaAccessSnapshot): void {
    this.snapshot = next;
    for (const l of [...this.listeners]) l();
  }
}

export type SensitiveStatus = 'idle' | 'loading' | 'ready' | 'error' | 'refreshing' | 'blocked';

export interface SensitiveSnapshot<T> {
  status: SensitiveStatus;
  value: T | null;
  error: ErrorView | null;
}

const IDLE = { status: 'idle', value: null, error: null } as const;

/**
 * One sensitive GET for a mounted view. Cleared synchronously whenever the path changes or the
 * access epoch moves; a response is accepted only if its request started under the current
 * epoch and path (late responses after a revocation are discarded).
 */
export class SensitiveResource<T> {
  private snapshot: SensitiveSnapshot<T>;
  private readonly listeners = new Set<() => void>();
  private controller: AbortController | null = null;
  private token = 0;
  private path: string | null;
  private active = false;
  private unsubscribeAccess: (() => void) | null = null;
  private seenEpoch: number;

  constructor(private readonly api: Pick<ApiClient, 'request'>, private readonly access: MediaAccess, initialPath: string | null) {
    this.path = initialPath;
    this.seenEpoch = access.getSnapshot().epoch;
    this.snapshot = this.idleFor(initialPath);
  }

  getSnapshot = (): SensitiveSnapshot<T> => this.snapshot;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  /** Mount (safe to call again after deactivate, e.g. under React StrictMode). */
  activate(): void {
    if (this.active) return;
    this.active = true;
    this.unsubscribeAccess = this.access.subscribe(() => this.onAccess());
    this.seenEpoch = this.access.getSnapshot().epoch;
    this.load();
  }

  deactivate(): void {
    this.active = false;
    this.unsubscribeAccess?.();
    this.unsubscribeAccess = null;
    this.cancel();
  }

  setPath(path: string | null): void {
    if (path === this.path) return;
    this.path = path;
    this.cancel();
    this.set(this.idleFor(path));
    if (this.active) this.load();
  }

  reload(): void {
    if (this.active) this.load();
  }

  private onAccess(): void {
    const epoch = this.access.getSnapshot().epoch;
    if (epoch === this.seenEpoch) return;
    this.seenEpoch = epoch;
    this.cancel();
    this.set(this.idleFor(this.path));
    this.load();
  }

  private idleFor(path: string | null): SensitiveSnapshot<T> {
    const access = this.access.getSnapshot().state;
    if (access !== 'ready') return { status: access, value: null, error: null };
    return path ? { status: 'loading', value: null, error: null } : IDLE;
  }

  private cancel(): void {
    this.token += 1;
    this.controller?.abort();
    this.controller = null;
  }

  private load(): void {
    const access = this.access.getSnapshot();
    const path = this.path;
    if (!this.active || !path || access.state !== 'ready') {
      this.set(this.idleFor(path));
      return;
    }
    this.cancel();
    const token = this.token;
    const epoch = access.epoch;
    const controller = new AbortController();
    this.controller = controller;
    this.set({ status: 'loading', value: null, error: null });
    const current = () => token === this.token && epoch === this.access.getSnapshot().epoch && this.access.getSnapshot().state === 'ready' && path === this.path;
    this.api.request<T>(path, { signal: controller.signal }).then(
      value => {
        if (current()) this.set({ status: 'ready', value, error: null });
      },
      error => {
        if (!current() || isAbortError(error)) return;
        this.set({ status: 'error', value: null, error: describeError(error, "Couldn't load this") });
      },
    );
  }

  private set(next: SensitiveSnapshot<T>): void {
    this.snapshot = next;
    for (const l of [...this.listeners]) l();
  }
}

export interface ControlDeps {
  access: MediaAccess;
  /** Re-reads GET /auth/session; true when the same signed-in session is still valid. */
  refreshSession: () => Promise<boolean>;
  reloadConfig: () => void;
  /** The session no longer satisfies policy (for example MFA): a fresh sign-in is required. */
  onPolicyChanged: (reason: string | null) => void;
}

/**
 * Applies server control events to the workspace. Sensitive views are cleared first, before any
 * network work. Only the latest media change may re-open access, so an older refresh that
 * finishes late cannot undo a newer revocation.
 */
export function createControlHandler(deps: ControlDeps): (event: { type: string; reason: string | null }) => Promise<void> {
  let sequence = 0;
  return async event => {
    switch (event.type) {
      case 'media.access_changed': {
        const mine = ++sequence;
        deps.access.invalidate('refreshing', event.reason ?? 'media_access_changed');
        let valid = false;
        try {
          valid = await deps.refreshSession();
        } catch {
          valid = false;
        }
        if (mine !== sequence) return;
        if (!valid) {
          deps.access.invalidate('blocked', 'session_refresh_failed');
          return;
        }
        deps.reloadConfig();
        deps.access.ready();
        return;
      }
      case 'auth.policy_changed':
        sequence += 1;
        deps.access.invalidate('blocked', event.reason ?? 'policy_changed');
        deps.onPolicyChanged(event.reason);
        return;
      case 'session.expired':
        sequence += 1;
        deps.access.invalidate('blocked', 'session_expired');
        return;
      default:
        // Unknown control events are ignored; the server stays authoritative.
        return;
    }
  };
}
